// AP Calculus AB - Unit 1: Limits and Continuity

export const unit1Lessons = [
  {
    id: 'limits-concept',
    title: 'The Concept of a Limit',
    unit: 'Unit 1: Limits and Continuity',
    description: 'Define limits verbally and graphically. Estimate limit values from tables using numerical approaches.',
    standards: ['AP College Board Calculus AB/BC', 'Common Core HSF-IF.C.7', 'NCTM Process Standards'],
    vocabulary: [
      { term: 'Limit', definition: 'The value that a function approaches as the input approaches a certain value' },
      { term: 'Left-hand limit', definition: 'The value f(x) approaches as x approaches c from the left (x < c)' },
      { term: 'Right-hand limit', definition: 'The value f(x) approaches as x approaches c from the right (x > c)' },
      { term: 'Two-sided limit', definition: 'Exists when left-hand and right-hand limits are equal' },
      { term: 'Numerical approach', definition: 'Estimating limits by creating tables of values approaching the point' }
    ],
    conceptExplanation: `The concept of a limit is the foundation of calculus. A limit describes the behavior of a function as the input approaches a particular value, without necessarily reaching that value.

**The Rule of Four for Limits:**

1. **Algebraically**: lim(x→c) f(x) = L means f(x) gets arbitrarily close to L as x gets arbitrarily close to c

2. **Graphically**: On a graph, trace the curve from both sides as x approaches c. Where does y approach?

3. **Numerically**: Create a table with x-values approaching c from both sides and observe f(x) values

4. **Verbally**: "As x gets closer and closer to c, f(x) gets closer and closer to L"

**Key Concepts:**
- The limit may exist even if f(c) is undefined
- Left-hand and right-hand limits must match for the two-sided limit to exist
- Notation: lim(x→c⁻) means from the left, lim(x→c⁺) means from the right`,
    workedExample: {
      problem: 'Estimate lim(x→2) (x² - 4)/(x - 2) using numerical and graphical approaches.',
      steps: [
        {
          step: 'Step 1: Create a numerical table approaching x = 2 from both sides',
          work: `From the left (x < 2):
x = 1.9:  f(1.9) = (1.9² - 4)/(1.9 - 2) = (3.61 - 4)/(-0.1) = -0.39/(-0.1) = 3.9
x = 1.99: f(1.99) = (1.99² - 4)/(1.99 - 2) = (3.9601 - 4)/(-0.01) = 3.99
x = 1.999: f(1.999) ≈ 3.999

From the right (x > 2):
x = 2.1:  f(2.1) = (2.1² - 4)/(2.1 - 2) = (4.41 - 4)/(0.1) = 0.41/0.1 = 4.1
x = 2.01: f(2.01) = (2.01² - 4)/(2.01 - 2) = (4.0401 - 4)/(0.01) = 4.01
x = 2.001: f(2.001) ≈ 4.001`
        },
        {
          step: 'Step 2: Analyze the numerical pattern',
          work: `As x approaches 2 from both sides, f(x) approaches 4.
Left-hand limit: lim(x→2⁻) f(x) = 4
Right-hand limit: lim(x→2⁺) f(x) = 4`
        },
        {
          step: 'Step 3: Verify graphically',
          work: `Graph y = (x² - 4)/(x - 2). Notice:
- The function has a hole (removable discontinuity) at x = 2
- The curve approaches the point (2, 4) from both sides
- Even though f(2) is undefined, the limit exists`
        },
        {
          step: 'Step 4: State the conclusion',
          work: `Since both one-sided limits equal 4, the two-sided limit exists:
lim(x→2) (x² - 4)/(x - 2) = 4

Note: We can verify algebraically by factoring: (x² - 4)/(x - 2) = (x - 2)(x + 2)/(x - 2) = x + 2 for x ≠ 2`
        }
      ]
    },
    teacherTip: 'When introducing limits, emphasize that "approaching" is different from "equaling." Use the analogy of driving toward a city—you can get arbitrarily close without actually arriving. Always have students check both one-sided limits before concluding the two-sided limit exists.',
    questions: [
      {
        id: 1,
        question: 'What is lim(x→3) (x + 5)?',
        options: ['3', '5', '8', 'Does not exist'],
        correctAnswer: 2,
        explanation: 'For continuous functions like linear functions, the limit equals the function value: lim(x→3) (x + 5) = 3 + 5 = 8. Direct substitution works when the function is continuous at the point.'
      },
      {
        id: 2,
        question: 'Given the table: x = 1.9, 1.99, 1.999 → f(x) = 5.7, 5.97, 5.997 and x = 2.1, 2.01, 2.001 → f(x) = 6.3, 6.03, 6.003. What is lim(x→2) f(x)?',
        options: ['2', '5', '6', 'Does not exist'],
        correctAnswer: 2,
        explanation: 'Both one-sided limits approach 6. From the left: 5.7 → 5.97 → 5.997 → 6. From the right: 6.3 → 6.03 → 6.003 → 6. Since both approach the same value, lim(x→2) f(x) = 6.'
      },
      {
        id: 3,
        question: 'For what value of x does lim(x→c) (2x - 6) = 0?',
        options: ['x = 0', 'x = 2', 'x = 3', 'x = 6'],
        correctAnswer: 2,
        explanation: 'Set 2x - 6 = 0 and solve: 2x = 6, so x = 3. For continuous linear functions, lim(x→c) f(x) = f(c), so lim(x→3) (2x - 6) = 2(3) - 6 = 0.'
      },
      {
        id: 4,
        question: 'If lim(x→5⁻) f(x) = 3 and lim(x→5⁺) f(x) = 7, what can you conclude about lim(x→5) f(x)?',
        options: ['It equals 3', 'It equals 7', 'It equals 5', 'It does not exist'],
        correctAnswer: 3,
        explanation: 'For a two-sided limit to exist, the left-hand and right-hand limits must be equal. Since 3 ≠ 7, the two-sided limit does not exist at x = 5.'
      },
      {
        id: 5,
        question: 'What is lim(x→0) x²?',
        options: ['0', '1', '2', 'Undefined'],
        correctAnswer: 0,
        explanation: 'For the continuous function f(x) = x², we can use direct substitution: lim(x→0) x² = 0² = 0. As x approaches 0 from either side, x² approaches 0.'
      },
      {
        id: 6,
        question: 'Graphically, if a function has a hole at x = 2 but approaches y = 5 from both sides, what is lim(x→2) f(x)?',
        options: ['2', '5', 'Undefined', 'Does not exist'],
        correctAnswer: 1,
        explanation: 'The limit depends on what the function approaches, not what it equals. Even though there is a hole (f(2) is undefined), if the function approaches 5 from both sides, then lim(x→2) f(x) = 5.'
      },
      {
        id: 7,
        question: 'Estimate lim(x→1) (x³ - 1)/(x - 1) by evaluating f(0.99) and f(1.01).',
        options: ['Approximately 2', 'Approximately 3', 'Approximately 4', 'Does not exist'],
        correctAnswer: 1,
        explanation: 'f(0.99) = (0.99³ - 1)/(0.99 - 1) = (0.970299 - 1)/(-0.01) = -0.029701/(-0.01) ≈ 2.97. f(1.01) = (1.01³ - 1)/(1.01 - 1) = (1.030301 - 1)/(0.01) ≈ 3.03. Both approach 3. (Note: algebraically, this factors to x² + x + 1, which equals 3 at x = 1.)'
      },
      {
        id: 8,
        question: 'What is lim(x→4) √x?',
        options: ['2', '4', '16', 'Undefined'],
        correctAnswer: 0,
        explanation: 'For the continuous function f(x) = √x on [0, ∞), use direct substitution: lim(x→4) √x = √4 = 2. The square root function is continuous at x = 4.'
      },
      {
        id: 9,
        question: 'If f(x) = |x|/x, what is lim(x→0) f(x)?',
        options: ['0', '1', '-1', 'Does not exist'],
        correctAnswer: 3,
        explanation: 'For x > 0: f(x) = x/x = 1, so lim(x→0⁺) f(x) = 1. For x < 0: f(x) = -x/x = -1, so lim(x→0⁻) f(x) = -1. Since the one-sided limits differ (1 ≠ -1), the limit does not exist.'
      },
      {
        id: 10,
        question: 'What is lim(h→0) [(3 + h)² - 9]/h?',
        options: ['0', '3', '6', '9'],
        correctAnswer: 2,
        explanation: 'Expand: [(3 + h)² - 9]/h = [9 + 6h + h² - 9]/h = [6h + h²]/h = h(6 + h)/h = 6 + h for h ≠ 0. As h → 0, this approaches 6. This is the derivative of x² at x = 3.'
      },
      {
        id: 11,
        question: 'Numerically, if as x approaches 2, f(x) values oscillate between 3 and 5 without settling, what is lim(x→2) f(x)?',
        options: ['3', '4', '5', 'Does not exist'],
        correctAnswer: 3,
        explanation: 'If the function values do not approach a single value but continue to oscillate, the limit does not exist. A limit requires the function to approach one specific value as x approaches the point.'
      },
      {
        id: 12,
        question: 'What is lim(x→-2) (x² + 4x + 4)/(x + 2)?',
        options: ['-2', '0', '2', 'Does not exist'],
        correctAnswer: 1,
        explanation: 'Factor the numerator: (x + 2)²/(x + 2) = x + 2 for x ≠ -2. So lim(x→-2) (x + 2) = -2 + 2 = 0. This is a removable discontinuity.'
      },
      {
        id: 13,
        question: 'If lim(x→c) f(x) = L and lim(x→c) g(x) = M, what is lim(x→c) [f(x) + g(x)]?',
        options: ['L', 'M', 'L + M', 'LM'],
        correctAnswer: 2,
        explanation: 'By the Sum Rule for Limits: The limit of a sum equals the sum of the limits. Therefore, lim(x→c) [f(x) + g(x)] = L + M.'
      },
      {
        id: 14,
        question: 'What is lim(x→0) sin(x)/x? (Use numerical estimation: try x = 0.1, 0.01, 0.001)',
        options: ['0', '0.5', '1', 'Does not exist'],
        correctAnswer: 2,
        explanation: 'This is a famous trigonometric limit. Numerically: sin(0.1)/0.1 ≈ 0.9983, sin(0.01)/0.01 ≈ 0.99998, sin(0.001)/0.001 ≈ 0.9999998. The values approach 1. This is proven rigorously using the squeeze theorem.'
      },
      {
        id: 15,
        question: 'For f(x) = (x² - 9)/(x - 3), what value does the function approach as x → 3?',
        options: ['0', '3', '6', 'Undefined'],
        correctAnswer: 2,
        explanation: 'Factor: (x² - 9)/(x - 3) = (x - 3)(x + 3)/(x - 3) = x + 3 for x ≠ 3. As x → 3, this approaches 3 + 3 = 6. Even though f(3) is undefined, the limit exists and equals 6.'
      },
      {
        id: 16,
        question: 'What is lim(x→5) 7?',
        options: ['0', '5', '7', 'Does not exist'],
        correctAnswer: 2,
        explanation: 'The limit of a constant function is the constant itself, regardless of what x approaches. For f(x) = 7, lim(x→c) f(x) = 7 for any value of c.'
      },
      {
        id: 17,
        question: 'If a table shows x approaching 4 from the left with f(x) = 10, 11, 12, 13, and from the right with f(x) = 10, 11, 12, 13, what is lim(x→4) f(x)?',
        options: ['4', '10', 'Does not exist', 'Cannot determine from table'],
        correctAnswer: 3,
        explanation: 'The table shows f(x) continuously increasing as x approaches 4, not approaching a single value. This suggests the function may be increasing without bound or the table does not show values close enough to x = 4. We cannot determine the limit from this information.'
      },
      {
        id: 18,
        question: 'What is lim(x→0⁺) 1/x?',
        options: ['0', '1', '+∞', 'Does not exist'],
        correctAnswer: 2,
        explanation: 'As x approaches 0 from the right (positive values), 1/x becomes increasingly large: 1/0.1 = 10, 1/0.01 = 100, 1/0.001 = 1000. The limit is +∞, meaning the function increases without bound.'
      },
      {
        id: 19,
        question: 'For g(x) = (√(x + 4) - 2)/x, estimate lim(x→0) g(x) using x = 0.01.',
        options: ['Approximately 0.25', 'Approximately 0.5', 'Approximately 1', 'Approximately 2'],
        correctAnswer: 0,
        explanation: 'g(0.01) = (√4.01 - 2)/0.01 = (2.00249... - 2)/0.01 ≈ 0.00249/0.01 ≈ 0.249 ≈ 0.25. (Algebraically, this can be shown to equal 1/4 using rationalization.)'
      },
      {
        id: 20,
        question: 'What does it mean verbally to say lim(x→2) f(x) = 5?',
        options: ['f(2) = 5', 'f(x) equals 5 when x is near 2', 'As x gets closer to 2, f(x) gets closer to 5', 'f(x) is always 5'],
        correctAnswer: 2,
        explanation: 'The verbal interpretation of a limit is: "As x gets arbitrarily close to 2 (from either side), f(x) gets arbitrarily close to 5." This does not require f(2) = 5; the limit describes behavior near the point, not at the point.'
      },
      {
        id: 21,
        question: 'What is lim(x→1) (x³ + 2x - 3)?',
        options: ['0', '1', '3', 'Does not exist'],
        correctAnswer: 0,
        explanation: 'For polynomial functions (which are continuous everywhere), use direct substitution: lim(x→1) (x³ + 2x - 3) = 1³ + 2(1) - 3 = 1 + 2 - 3 = 0.'
      },
      {
        id: 22,
        question: 'If lim(x→3⁻) f(x) = 4 and lim(x→3⁺) f(x) = 4, but f(3) = 7, what is lim(x→3) f(x)?',
        options: ['3', '4', '7', 'Does not exist'],
        correctAnswer: 1,
        explanation: 'The limit depends only on what the function approaches, not what it equals at the point. Since both one-sided limits equal 4, lim(x→3) f(x) = 4, even though f(3) = 7.'
      },
      {
        id: 23,
        question: 'What is lim(x→0) (1 - cos(x))/x? (Estimate numerically with x = 0.1)',
        options: ['Approximately 0', 'Approximately 0.05', 'Approximately 0.5', 'Approximately 1'],
        correctAnswer: 1,
        explanation: '(1 - cos(0.1))/0.1 = (1 - 0.995)/0.1 = 0.005/0.1 = 0.05. As x gets smaller, this approaches 0. Using calculus techniques (L\'Hôpital\'s Rule or Taylor series), the exact limit is 0.'
      },
      {
        id: 24,
        question: 'For h(x) = {x² if x < 1; 2x if x ≥ 1}, what is lim(x→1) h(x)?',
        options: ['1', '2', 'Does not exist', 'Cannot be determined'],
        correctAnswer: 2,
        explanation: 'From the left (x < 1): lim(x→1⁻) h(x) = lim(x→1⁻) x² = 1² = 1. From the right (x ≥ 1): lim(x→1⁺) h(x) = lim(x→1⁺) 2x = 2(1) = 2. Since 1 ≠ 2, the limit does not exist.'
      },
      {
        id: 25,
        question: 'What is lim(x→4) (x - 4)/(√x - 2)?',
        options: ['0', '2', '4', '8'],
        correctAnswer: 2,
        explanation: 'Rationalize by multiplying by (√x + 2)/(√x + 2): [(x - 4)(√x + 2)]/[(√x - 2)(√x + 2)] = [(x - 4)(√x + 2)]/(x - 4) = √x + 2 for x ≠ 4. As x → 4, this approaches √4 + 2 = 2 + 2 = 4.'
      },
      {
        id: 26,
        question: 'A graph shows a jump at x = 2, with the function approaching 3 from the left and 5 from the right. What is lim(x→2) f(x)?',
        options: ['2', '3', '4', 'Does not exist'],
        correctAnswer: 3,
        explanation: 'This is a jump discontinuity. Since lim(x→2⁻) f(x) = 3 and lim(x→2⁺) f(x) = 5, and 3 ≠ 5, the two-sided limit does not exist.'
      },
      {
        id: 27,
        question: 'What is lim(x→0) |x|?',
        options: ['0', '1', '-1', 'Does not exist'],
        correctAnswer: 0,
        explanation: 'From the left: lim(x→0⁻) |x| = lim(x→0⁻) (-x) = 0. From the right: lim(x→0⁺) |x| = lim(x→0⁺) x = 0. Both one-sided limits equal 0, so lim(x→0) |x| = 0. The absolute value function is continuous at 0.'
      },
      {
        id: 28,
        question: 'Using a table, as x → 0: f(-0.1) = 2.1, f(-0.01) = 2.01, f(0.01) = 2.01, f(0.1) = 2.1. What is lim(x→0) f(x)?',
        options: ['0', '1', '2', 'Does not exist'],
        correctAnswer: 2,
        explanation: 'From both sides, the function values approach 2 (2.1 → 2.01 → 2.00... as we get closer to 0). Therefore, lim(x→0) f(x) = 2.'
      },
      {
        id: 29,
        question: 'What is lim(x→π) sin(x)?',
        options: ['0', '1', 'π', 'Undefined'],
        correctAnswer: 0,
        explanation: 'For continuous functions like sine, use direct substitution: lim(x→π) sin(x) = sin(π) = 0. The sine function is continuous everywhere.'
      },
      {
        id: 30,
        question: 'If lim(x→c) f(x) = 8 and lim(x→c) g(x) = 2, what is lim(x→c) [f(x)/g(x)]?',
        options: ['2', '4', '6', '10'],
        correctAnswer: 1,
        explanation: 'By the Quotient Rule for Limits (when the denominator\'s limit is not zero): lim(x→c) [f(x)/g(x)] = [lim(x→c) f(x)] / [lim(x→c) g(x)] = 8/2 = 4.'
      }
    ]
  }
  // Additional lessons for Unit 1 will be added...
];